import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { adminHeaders, api } from "../../lib/api";

interface ActivitySettingRow {
  id?: number;
  activity_code?: string;
  setting_key?: string;
  setting_json?: string;
  is_enabled?: number;
  is_frontend_visible?: number;
  sort_order?: number;
  version?: string;
  note?: string | null;
  updated_at?: string;
  [key: string]: unknown;
}

interface LineLinkSettings {
  line_guild_leader_url: string;
  line_official_url: string;
  line_group_checkin_url: string;
  invite_share_text: string;
  group_checkin_instruction_text: string;
}

const LINE_LINK_ACTIVITY_CODE = "public_links";
const LINE_LINK_SETTING_KEY = "line_links";

const emptyLineLinkSettings: LineLinkSettings = {
  line_guild_leader_url: "",
  line_official_url: "",
  line_group_checkin_url: "",
  invite_share_text: "",
  group_checkin_instruction_text: ""
};

interface OverviewCard {
  title: string;
  code: string;
  groupCode?: string;
  targetId?: string;
  displayCode?: string;
  threshold: string;
  reward: string;
  detail: string;
  status?: string;
  frontend?: string;
  version?: string;
}

const activityNames: Record<string, string> = {
  newbie: "新人七關設定",
  gems: "寶石碎片設定",
  "red-packet": "紅包券 / 紅包池設定",
  invite: "邀友支線設定",
  "weekly-pass": "週通行證設定",
  "graduate-week": "畢業首週設定",
  "regular-rebate": "正式成員常態回饋設定",
  "weekend-boost": "週末耀旺 V1 設定（weekendBoost legacy key）",
  banner: "Banner 設定",
  "limited-challenge": "月初爆分挑戰"
};

const overviewCards: OverviewCard[] = [
  { title: "新人七關設定", code: "newbie", threshold: "首存 70% + 10% + 10% + 10%，二存 80%，三存 50%", reward: "河幣 / 寶石 / 紅包券 / 畢業禮 / 閃耀值", detail: "查看新人七關設定" },
  { title: "寶石碎片設定", code: "gems", threshold: "V1 只開放 3 枚兌換", reward: "newbie_bonus_red_packet 新手加碼紅包券", detail: "查看寶石兌換表" },
  { title: "紅包券 / 紅包池設定", code: "red-packet", threshold: "紅包券抽獎結果才進 rewards", reward: "紅包券抽獎 V1 已完成；紅包池後台權重 TODO", detail: "查看券種與獎池", status: "抽獎 V1 已完成", frontend: "前台紅包券可抽 / 權重後台待正式化", version: "V1 + TODO" },
  { title: "邀友支線設定", code: "invite", threshold: "好友首存為目前主要完成項", reward: "好友首存 50,000 部分完成；入盟 6,666 / 二存 30,000 TODO", detail: "查看邀友任務", status: "部分完成 / 預留", version: "V1 部分完成" },
  { title: "週通行證設定", code: "weekly-pass", threshold: "畢業首週結束後下一個週一開啟，每週最多 5 點", reward: "5 任務：打卡、充值 1,000、紅包抽獎、查看會員身份、查看邀友支線；2/3/5 點給寶石、週耀券、閃耀值", detail: "查看週里程碑" },
  { title: "畢業首週設定", code: "graduate-week", threshold: "畢業後首週 7 天", reward: "打卡給寶石；單筆 1,000 給畢業啟航券；累積 5,000 給高級啟航券；10,000 / 30,000 / 50,000 為 V2 尚未開放", detail: "查看畢業首週" },
  { title: "正式成員常態回饋", code: "regular-rebate", threshold: "畢業後正式存款 1,000 以上，deposited_at >= graduation_at", reward: "級距 5% / 8% / 12% / 14% / 15%，自動建立 rewards ready；新人首 / 二 / 三存不可建，週末用過仍可建", detail: "查看常態回饋級距", status: "啟用 / V1 已完成", frontend: "後台自動建單 / 派發中心人工確認", version: "V1" },
  { title: "週末耀旺 V1 發券版", code: "weekend-bonus", groupCode: "weekend-boost", targetId: "weekend-boost", threshold: "週六 00:00 至週日 23:59，週通行證正式開啟後", reward: "單筆 1,000 與累積 5,000 可由同一筆 5,000 完成；每週末最多 2 張，且不阻擋常態回饋", detail: "查看週末門檻", status: "啟用 / V1 已完成", frontend: "前台 /weekend-bonus", version: "V1" },
  { title: "Banner 設定", code: "banner", threshold: "頁面、排序、啟用、CTA、圖片 URL", reward: "只讀顯示，暫不接 R2 / 上傳", detail: "查看 Banner 欄位" }
];

const newbieSteps = [
  ["第 1 關", "後台選定入盟啟動筆", "首存單筆滿 1,000", "首存拆分", "70%", "彩 19 倍", "派發中心", "是"],
  ["第 2 關", "第 1 關完成後隔天 12:00", "回盟補給 / local dev 簽到", "首存拆分", "10%", "彩 19 倍", "派發中心", "是"],
  ["第 3 關", "第 2 關完成", "後台人工選定二存 deposit，單筆滿 1,000", "續存加碼", "80% + 寶石 +1", "彩 21 倍", "派發中心 + asset_ledgers", "是"],
  ["第 4 關", "第 3 關完成後隔天 12:00", "入盟試煉 / 問答", "首存拆分", "10%", "彩 19 倍", "派發中心", "是"],
  ["第 5 關", "第 4 關完成後", "後台人工選定三存 deposit，單筆滿 1,000", "續存加碼", "50% + 寶石 +1 + 新人啟航紅包券", "彩 29 倍", "派發中心 + voucher_wallets", "是"],
  ["第 6 關", "第 5 關完成後", "使用 newbie_launch_red_packet 抽紅包", "紅包抽中河幣 + 寶石碎片", "抽中結果 + 寶石 +1", "依紅包抽獎", "抽中結果進派發中心", "是"],
  ["第 7 關", "第 6 關完成後", "聚耀畢業，建立兩筆派發", "首存最後 10% + 畢業禮", "10% + 8,888 + 閃耀值 +5", "彩 19 倍", "兩筆 completed 後 graduated", "是"]
] as const;

const gemSources = [
  "新人第 3 關完成：+1",
  "新人第 5 關完成：+1",
  "新人第 6 關抽完紅包：+1",
  "畢業首週打卡：+1",
  "週通行證 2 點：+1"
];

const gemExchanges = [
  ["3 枚", "newbie_bonus_red_packet 新手加碼紅包券", "V1 開放", "v1", "顯示", "1"],
  ["5 枚", "週耀紅包券", "V2 / 尚未開放", "v2", "可保留但需標示", "2"],
  ["10 枚", "輪盤資格", "V2 / 尚未開放", "v2", "可保留但需標示", "3"],
  ["20 枚", "高級紅包券", "V2 / 尚未開放", "v2", "可保留但需標示", "4"]
] as const;

const voucherTypes = [
  ["新人啟航紅包券", "新人第 5 關完成後核發，用於第 6 關", "newbie_launch_red_packet", "啟用", "顯示", "依活動", "每人限制"],
  ["新手加碼紅包券", "寶石 3 枚兌換", "newbie_bonus_red_packet", "啟用", "顯示", "依活動", "每人限制"],
  ["畢業啟航紅包券", "畢業首週單筆 1,000", "graduate_launch_red_packet", "啟用", "顯示", "依活動", "每人限制"],
  ["高級啟航紅包券", "畢業首週累積 5,000", "advanced_launch_red_packet", "啟用", "顯示", "依活動", "每人限制"],
  ["週耀紅包券", "週通行證 3 點獎勵", "weekly_bright_red_packet", "啟用", "顯示", "每週", "每週限制"],
  ["週末耀旺紅包券", "週末耀旺單筆滿 1,000", "weekend_bright_red_packet", "啟用", "顯示", "每週末", "每週末 1 張"],
  ["高級週末紅包券", "週末耀旺累積滿 5,000", "weekend_advanced_red_packet", "啟用", "顯示", "每週末", "每週末 1 張"]
] as const;

const redPacketItems = [
  ["開盟紅包", "3,000", "38", "彩 8 倍", "V1 可抽", "後台權重 TODO", "v1"],
  ["聚氣紅包", "5,000", "28", "彩 8 倍", "V1 可抽", "後台權重 TODO", "v1"],
  ["耀財紅包", "6,666", "15", "彩 8 倍", "V1 可抽", "後台權重 TODO", "v1"],
  ["耀旺紅包", "8,888", "10", "彩 8 倍", "V1 可抽", "後台權重 TODO", "v1"],
  ["鼎立紅包", "18,888", "5", "彩 10 倍", "V1 可抽", "後台權重 TODO", "v1"],
  ["長盛紅包", "28,888", "3", "彩 10 倍", "V1 可抽", "後台權重 TODO", "v1"],
  ["鼎盛紅包", "58,888", "1", "彩 10 倍", "V1 可抽", "後台權重 TODO", "v1"]
] as const;

const inviteTasks = [
  ["好友入盟", "好友", "完成入盟", "6,666", "是", "TODO", "尚未完成"],
  ["好友首存", "邀請人", "好友首存單筆滿 1,000", "50,000", "是", "V1", "部分完成 / 目前主要完成項"],
  ["好友第二筆充值", "邀請人", "好友第二筆充值單筆滿 1,000", "30,000", "是", "TODO", "尚未完成"],
  ["本月邀 1 位新好友首存", "邀請人", "畢業後裂變", "閃耀值 +5", "否", "V2", "預留"],
  ["本月邀 3 位新好友首存", "邀請人", "畢業後裂變", "寶石碎片 +3", "否", "V2", "預留"],
  ["本月邀 5 位新好友首存", "邀請人", "畢業後裂變", "高級紅包券 1 張", "是", "V2", "預留"]
] as const;

const weeklyTasks = [
  ["每週打卡 1 次", "+1"],
  ["本週單筆充值滿 1,000", "+1"],
  ["本週使用任一紅包券抽獎 1 次", "+1"],
  ["本週查看會員身份 / 公會等級 1 次", "+1"],
  ["本週查看邀友支線 1 次", "+1"]
] as const;

const weeklyMilestones = [
  ["2 點", "寶石碎片 +1", "啟用", "顯示"],
  ["3 點", "weekly_bright_red_packet 週耀紅包券 +1", "啟用", "顯示"],
  ["5 點", "閃耀值 +5", "啟用", "顯示"]
] as const;

const graduateTasks = [
  ["畢業打卡", "畢業後首週", "寶石碎片 +1", "啟用", "V1"],
  ["畢業首週單筆充值滿 1,000", "單筆 1,000", "畢業啟航紅包券 1 張", "啟用", "V1"],
  ["畢業首週累積充值滿 5,000", "累積 5,000", "高級啟航紅包券 1 張", "啟用", "V1"],
  ["畢業首週累積充值滿 10,000", "累積 10,000", "輪盤資格 1 次", "尚未開放", "V2 預留"],
  ["畢業首週累積充值滿 30,000", "累積 30,000", "高額加碼券資格", "尚未開放", "V2 預留"],
  ["畢業首週累積充值滿 50,000", "累積 50,000", "會長關懷標記 / 尊享紅包券", "尚未開放", "V2 預留"]
] as const;

const weekendThresholds = [
  ["週末打卡 / 查看週末耀旺", "完成週末任務紀錄", "每人每週末限一次", "啟用"],
  ["週末單筆充值滿 1,000", "weekend_bright_red_packet 週末耀旺紅包券 1 張", "每人每週末限一次", "啟用"],
  ["週末累積充值滿 5,000", "weekend_advanced_red_packet 高級週末紅包券 1 張", "每人每週末限一次", "啟用"]
] as const;

const regularRebateTiers = [
  ["1,000 以上", "5%", "deposit.amount × 100 × 5%"],
  ["5,000 以上", "8%", "deposit.amount × 100 × 8%"],
  ["10,000 以上", "12%", "deposit.amount × 100 × 12%"],
  ["30,000 以上", "14%", "deposit.amount × 100 × 14%"],
  ["50,000 以上", "15%", "deposit.amount × 100 × 15%"]
] as const;

const regularRebateRules = [
  "只適用已畢業正式會員，member.status = graduated。",
  "member.graduation_at 必須有值。",
  "只吃畢業後正式存款，deposit.deposited_at 必須 >= graduation_at。",
  "符合條件時自動建立 regular_deposit_rebate，reward_status = ready。",
  "派發中心人工確認後才 completed，不會自動 completed。",
  "週末耀旺用過的 deposit 仍可建立常態回饋。",
  "新人首存 / 二存 / 三存使用過的 deposit 不可建立常態回饋。",
  "畢業前額外存款不自動建立常態回饋。"
];

const weekendRules = [
  "週末限定，時間為每週六 00:00 至週日 23:59 Asia/Taipei。",
  "已畢業會員才可參加。",
  "畢業首週結束後、週通行證正式開啟後才可參加。",
  "週末單筆滿 1,000 → weekend_bright_red_packet 週末耀旺紅包券 1 張。",
  "週末累積滿 5,000 → weekend_advanced_red_packet 高級週末紅包券 1 張。",
  "一筆 5,000 週末 deposit 可以同時完成單筆滿 1,000 任務與累積滿 5,000 任務。",
  "每位會員每個週末最多 2 張週末券。",
  "存 50,000 不會自動變多張券。",
  "週末耀旺是額外加碼，不阻擋常態回饋。",
  "紅包券本身不進 rewards，抽中河幣後才建立 rewards。"
];

const weeklyPassRules = [
  "週通行證每週一 00:00 開始，週日 23:59 結束。",
  "週通行證與畢業首週不重疊。",
  "畢業首週結束後下一個週一 00:00 才開啟。",
  "週通行證存款任務只是點數任務，不消耗 deposit，不阻擋常態回饋，也不阻擋週末耀旺。"
];

const newbieDepositRules = [
  "二存必須後台人工選擇 deposit。",
  "三存必須後台人工選擇 deposit。",
  "不可以由爬蟲自動判定第 2 筆 / 第 3 筆。",
  "被選為新人首存 / 二存 / 三存的 deposit，不可建立常態回饋。",
  "畢業前額外存款不自動建立常態回饋。"
];

const redPacketRules = [
  "紅包券本身不進 rewards。",
  "抽中河幣後才建立 rewards。",
  "rewards 是目前 V1 主派發中心。",
  "reward_dispatches 是 legacy，不是目前主流程。"
];

const legacyItems = [
  ["weekly_glory", "舊週耀紅包券 key", "legacy，現行使用 weekly_bright_red_packet"],
  ["graduate_start", "舊畢業啟航紅包券 key", "legacy，現行使用 graduate_launch_red_packet"],
  ["advanced_start", "舊高級啟航紅包券 key", "legacy，現行使用 advanced_launch_red_packet"],
  ["weekend_packet", "舊週末紅包券 key", "legacy，現行使用 weekend_bright_red_packet / weekend_advanced_red_packet"],
  ["weekendBoost", "舊週末耀旺 helper / 入口命名", "legacy，現行主流程為 weekendBonus.ts / /weekend-bonus"],
  ["reward_dispatches", "舊派發流程", "legacy，現行 V1 主派發中心為 rewards"],
  ["drawRedPacket", "舊紅包池抽獎流程", "legacy，現行 V1 紅包券抽獎主流程為 drawVoucherRedPacket"],
  ["舊週末單筆 3,000 寶石", "舊週末規格", "legacy，不是目前主規格"],
  ["舊週末單筆 5,000 紅包", "舊週末規格", "legacy，不是目前主規格"],
  ["聚耀值", "舊名稱", "legacy，現行統一稱閃耀值"]
] as const;

const rescueTiers = [
  ["T1", "本月累積存款 5,000+", "本月淨負達 300,000 河幣+", "8,888"],
  ["T2", "本月累積存款 10,000+", "本月淨負達 600,000 河幣+", "18,888"],
  ["T3", "本月累積存款 20,000+", "本月淨負達 1,200,000 河幣+", "28,888"],
  ["T4", "本月累積存款 30,000+", "本月淨負達 1,800,000 河幣+", "38,888"],
  ["T5", "本月累積存款 50,000+", "本月淨負達 3,000,000 河幣+", "58,888"]
] as const;

const bannerFields = [
  "banner_title",
  "banner_subtitle",
  "banner_body",
  "banner_image_url",
  "banner_cta_text",
  "banner_cta_link",
  "banner_enabled",
  "banner_sort_order",
  "banner_page"
];

const bannerPages = ["login", "lobby", "calculator", "newbie", "red_packet", "gems", "weekly_pass", "graduate_week", "weekend_bonus", "weekend_boost（legacy）"];

export function AdminSettingsPage() {
  const [rows, setRows] = useState<ActivitySettingRow[]>([]);
  const [error, setError] = useState("");
  const [lineSettings, setLineSettings] = useState<LineLinkSettings>(emptyLineLinkSettings);
  const [lineDraft, setLineDraft] = useState<LineLinkSettings>(emptyLineLinkSettings);
  const [lineMessage, setLineMessage] = useState("");
  const [lineError, setLineError] = useState("");
  const [savingLineSettings, setSavingLineSettings] = useState(false);

  useEffect(() => {
    api<ActivitySettingRow[]>("/api/admin/activity-settings", { headers: adminHeaders() })
      .then((data) => {
        setRows(data);
        const row = data.find((item) =>
          item.activity_code === LINE_LINK_ACTIVITY_CODE
          && item.setting_key === LINE_LINK_SETTING_KEY
          && (item.version ?? "v1") === "v1"
        );
        const settings = normalizeLineLinkSettings(parseSettingJson(row?.setting_json));
        setLineSettings(settings);
        setLineDraft(settings);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "活動設定讀取失敗"));
  }, []);

  const grouped = useMemo(() => groupRows(rows), [rows]);

  function updateLineDraft(key: keyof LineLinkSettings, value: string) {
    setLineDraft((current) => ({ ...current, [key]: value }));
    setLineMessage("");
    setLineError("");
  }

  function resetLineSettings() {
    setLineDraft(lineSettings);
    setLineMessage("");
    setLineError("");
  }

  async function saveLineSettings() {
    setLineMessage("");
    setLineError("");
    const validation = validateLineLinkSettings(lineDraft);
    if (validation) {
      setLineError(validation);
      return;
    }
    setSavingLineSettings(true);
    try {
      await api("/api/admin/activity-settings", {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify({
          activityCode: LINE_LINK_ACTIVITY_CODE,
          settingKey: LINE_LINK_SETTING_KEY,
          settingJson: lineDraft,
          isEnabled: true,
          isFrontendVisible: true,
          version: "v1",
          note: "前台 LINE 連結與邀請文案"
        })
      });
      setLineSettings(lineDraft);
      setLineMessage("LINE 連結設定已儲存。");
    } catch (err) {
      setLineError(err instanceof Error ? err.message : "LINE 連結設定儲存失敗");
    } finally {
      setSavingLineSettings(false);
    }
  }

  return (
    <AdminLayout title="活動設定">
      <section className="settings-warning-panel">
        <h2>活動設定安全提醒</h2>
        <ul>
          <li>設定改動只影響新產生的活動項目。</li>
          <li>已建立或已完成的派發紀錄不會自動改變。</li>
          <li>已完成派發不可直接改金額。</li>
          <li>若要修正，只能新增補發或扣回紀錄，並寫入 admin_logs。</li>
          <li>本頁以目前已驗收的 V1 檔案邏輯展示為主；舊 key 會標示為 legacy，不再當成目前主規格。</li>
        </ul>
        <p>本頁第一版為只讀卡片化，不會送出設定保存、不會重算既有流程。</p>
      </section>

      {error ? <div className="error-box">{error}</div> : null}

      <section className="settings-section" id="settings-public-line-links">
        <div className="settings-section-head">
          <span>public_links / line_links</span>
          <h2>LINE 連結設定</h2>
          <p>這裡管理前台邀友頁與 LINE 群打卡頁會使用的公開 LINE 連結與文案；不存 token，也不回傳任何 secret。</p>
        </div>
        <div className="admin-level-form">
          <label>
            公會長 LINE 連結
            <input
              value={lineDraft.line_guild_leader_url}
              onChange={(event) => updateLineDraft("line_guild_leader_url", event.target.value)}
              placeholder="可留空；支援 lin.ee / line.me / page.line.me / line://"
            />
          </label>
          <label>
            官方 LINE 連結
            <input
              value={lineDraft.line_official_url}
              onChange={(event) => updateLineDraft("line_official_url", event.target.value)}
              placeholder="可留空；支援 lin.ee / line.me / page.line.me / line://"
            />
          </label>
          <label className="admin-form-wide">
            指定 LINE 群打卡連結
            <input
              value={lineDraft.line_group_checkin_url}
              onChange={(event) => updateLineDraft("line_group_checkin_url", event.target.value)}
              placeholder="可留空；前台未設定時會提示聯繫客服"
            />
          </label>
          <label className="admin-form-wide">
            邀請說詞
            <textarea
              rows={5}
              value={lineDraft.invite_share_text}
              onChange={(event) => updateLineDraft("invite_share_text", event.target.value)}
              placeholder="可留空；前台會使用既有預設邀請說詞"
            />
          </label>
          <label className="admin-form-wide">
            群打卡說明
            <textarea
              rows={4}
              value={lineDraft.group_checkin_instruction_text}
              onChange={(event) => updateLineDraft("group_checkin_instruction_text", event.target.value)}
              placeholder="可留空；本階段先儲存，不覆蓋 LINE 群打卡頁主文案"
            />
          </label>
          <div className="admin-form-wide settings-line-actions">
            <button className="primary-cta" type="button" disabled={savingLineSettings} onClick={saveLineSettings}>
              {savingLineSettings ? "儲存中..." : "儲存 LINE 設定"}
            </button>
            <button className="secondary-cta" type="button" disabled={savingLineSettings} onClick={resetLineSettings}>
              重設
            </button>
          </div>
        </div>
        {lineMessage ? <div className="result">{lineMessage}</div> : null}
        {lineError ? <div className="error-box">{lineError}</div> : null}
      </section>

      <section className="settings-section" id="settings-rescue-bonus">
        <div className="settings-section-head">
          <span>rescue_bonus</span>
          <h2>回穩補給金級距設定｜只讀</h2>
          <p>目前僅作為人工審核參考，不會自動判斷、不會自動派發。</p>
        </div>
        <div className="settings-subgrid">
          <InfoCard title="風險控管" items={["同月符合多個級距，擇高，不疊加。", "前台不顯示完整淨負條件。", "級距只供後台人工核對，不會直接決定審核結果。"]} />
          <InfoCard title="目前缺口" items={["尚未串接有效投注資料。", "尚未串接會員輸贏資料。", "尚未串接風控標籤。"]} />
        </div>
        <SimpleTable headers={["級距", "存款門檻", "淨負門檻", "參考補給金"]} rows={rescueTiers} />
      </section>

      <section className="settings-section">
        <div className="settings-section-head">
          <span>OVERVIEW</span>
          <h2>活動設定總覽</h2>
          <p>以營運可讀的方式彙整目前活動設定。原始 JSON 收在各區的進階資料中。</p>
        </div>
        <div className="settings-overview-grid">
          {overviewCards.map((card) => (
            <article className="settings-overview-card" key={card.code}>
              <div className="settings-card-topline">
                <span>{card.displayCode ?? card.code}</span>
                <StatusPill label={card.status ?? activityStatus(grouped[card.groupCode ?? card.code])} />
              </div>
              <h3>{card.title}</h3>
              <dl className="settings-mini-list">
                <div><dt>前台顯示</dt><dd>{card.frontend ?? frontendStatus(grouped[card.groupCode ?? card.code])}</dd></div>
                <div><dt>版本</dt><dd>{card.version ?? versionText(grouped[card.groupCode ?? card.code])}</dd></div>
                <div><dt>主要門檻</dt><dd>{card.threshold}</dd></div>
                <div><dt>主要獎勵</dt><dd>{card.reward}</dd></div>
                <div><dt>最後更新</dt><dd>{updatedText(grouped[card.groupCode ?? card.code])}</dd></div>
              </dl>
              <a className="settings-card-link" href={`#settings-${card.targetId ?? card.code}`}>{card.detail}</a>
            </article>
          ))}
        </div>
      </section>

      <SettingsSection id="newbie" title="新人七關設定" code="newbie" rows={grouped.newbie}>
        <div className="settings-subgrid">
          <InfoCard title="首存 100% 拆分" items={["第 1 關：70%", "第 2 關：10%", "第 4 關：10%", "第 7 關：10%"]} />
          <InfoCard title="續存加碼" items={["第 3 關：二存 80%", "第 5 關：三存 50%"]} />
          <InfoCard title="小額試玩 3%｜HOLD" items={["單筆未滿 1,000", "3%", "每會員限一次", "彩 8 倍", "目前狀態：暫未啟用", "此功能目前僅為預留規格，尚未建立 reward_type、API、防重複與派發流程，正式前不可使用。"]} />
          <InfoCard title="二存 / 三存選定規則" items={newbieDepositRules} />
        </div>
        <SimpleTable headers={["關卡", "開啟條件", "任務條件", "獎勵類型", "獎勵", "倍率備註", "狀態來源", "進派發中心"]} rows={newbieSteps} />
      </SettingsSection>

      <SettingsSection id="gems" title="寶石碎片設定" code="gems" rows={grouped.gems}>
        <InfoCard title="寶石來源" items={gemSources} />
        <SimpleTable headers={["需求碎片數", "兌換物", "開放狀態", "版本", "前台顯示", "排序"]} rows={gemExchanges} />
      </SettingsSection>

      <SettingsSection id="red-packet" title="紅包券 / 紅包池設定" code="red-packet" rows={grouped["red-packet"]} statusLabel="紅包券抽獎 V1 已完成" frontendLabel="前台紅包券可抽 / 紅包池權重待正式化" versionLabel="V1 + TODO">
        <div className="settings-subgrid">
          <InfoCard title="紅包券抽獎" items={["V1 已完成。", "支援目前已驗收券種。", "週末兩券走 drawVoucherRedPacket，不 fallback 到 legacy drawRedPacket。"]} />
          <InfoCard title="紅包池後台權重" items={["TODO / 尚未正式化。", "目前不要把權重表視為已完成後台設定。", "未來紅包池後台正式化時再接 DB 權重。"]} />
          <InfoCard title="V1 派發規則" items={redPacketRules} />
        </div>
        <SimpleTable headers={["券名稱", "取得方式", "對應獎池", "是否啟用", "前台顯示", "有效期", "每人限制"]} rows={voucherTypes} />
        <SimpleTable headers={["獎項名稱", "獎項金額", "參考權重", "倍率備註", "抽獎狀態", "後台權重", "版本"]} rows={redPacketItems} />
      </SettingsSection>

      <SettingsSection id="invite" title="邀友支線設定" code="invite" rows={grouped.invite} statusLabel="部分完成 / 預留" versionLabel="V1 部分完成">
        <SimpleTable headers={["任務名稱", "對象", "門檻", "獎勵", "進派發中心", "版本", "狀態"]} rows={inviteTasks} />
      </SettingsSection>

      <SettingsSection id="weekly-pass" title="週通行證設定" code="weekly-pass" rows={grouped["weekly-pass"]}>
        <div className="settings-subgrid">
          <InfoCard title="週期與開啟條件" items={weeklyPassRules} />
          <InfoCard title="目前 5 個任務" items={weeklyTasks.map(([name, points]) => `${name}：${points}`)} />
        </div>
        <SimpleTable headers={["點數需求", "獎勵", "是否啟用", "前台顯示"]} rows={weeklyMilestones} />
      </SettingsSection>

      <SettingsSection id="graduate-week" title="畢業首週設定" code="graduate-week" rows={grouped["graduate-week"]}>
        <div className="settings-subgrid">
          <InfoCard title="V1 已開放任務" items={["畢業打卡 → 寶石碎片 +1", "單筆充值滿 1,000 → graduate_launch_red_packet 畢業啟航紅包券", "累積充值滿 5,000 → advanced_launch_red_packet 高級啟航紅包券"]} />
          <InfoCard title="尚未開放" items={["累積 10,000 / 30,000 / 50,000 → V2 預留 / 尚未開放", "不要把 V2 資格視為目前已完成獎勵。"]} />
        </div>
        <SimpleTable headers={["任務名稱", "門檻", "獎勵", "是否啟用", "版本"]} rows={graduateTasks} />
      </SettingsSection>

      <SettingsSection id="regular-rebate" title="正式成員常態回饋設定" code="regular-rebate" rows={grouped["regular-rebate"]} statusLabel="啟用 / V1 已完成" frontendLabel="後台自動建單 / 派發中心人工確認" versionLabel="V1">
        <div className="settings-subgrid">
          <InfoCard title="建立與派發規則" items={regularRebateRules} />
          <InfoCard title="派發中心規則" items={["reward_type = regular_deposit_rebate", "自動建單只建立 ready", "後台人工確認後才 completed", "不使用 reward_dispatches"]} />
        </div>
        <SimpleTable headers={["單筆存款門檻", "回饋比例", "派發金額公式"]} rows={regularRebateTiers} />
      </SettingsSection>

      <SettingsSection id="weekend-boost" title="週末耀旺 V1 發券版設定" code="weekend-bonus" rows={grouped["weekend-boost"]} statusLabel="啟用 / V1 已完成" frontendLabel="/weekend-bonus" versionLabel="V1">
        <InfoCard title="主流程分界" items={["目前主流程：weekend-bonus。", "legacy：weekend-boost 是舊流程，不是目前主流程。"]} />
        <InfoCard title="目前規則" items={weekendRules} />
        <SimpleTable headers={["門檻", "獎勵", "每週限制", "是否啟用"]} rows={weekendThresholds} />
      </SettingsSection>

      <SettingsSection id="banner" title="Banner 設定" code="banner" rows={grouped.banner}>
        <div className="settings-subgrid">
          <InfoCard title="支援欄位" items={bannerFields} />
          <InfoCard title="支援頁面" items={bannerPages} />
          <InfoCard title="第一版範圍" items={["後台可讀卡片化顯示", "保留進階 JSON", "不做圖片上傳", "不接 R2"]} />
        </div>
      </SettingsSection>

      <section className="settings-section" id="settings-legacy">
        <div className="settings-section-head">
          <span>LEGACY</span>
          <h2>舊規格 / legacy 分界</h2>
          <p>以下項目保留供排查舊資料或舊設定使用，不是目前 V1 主流程。</p>
        </div>
        <SimpleTable headers={["項目", "舊用途", "目前狀態"]} rows={legacyItems} />
      </section>

      <section className="settings-section" id="settings-advanced-json">
        <div className="settings-section-head">
          <span>ADVANCED</span>
          <h2>進階 JSON 資料</h2>
          <p>此區保留全部原始設定 rows，供工程排查使用。</p>
        </div>
        <AdvancedJson rows={rows} />
      </section>
    </AdminLayout>
  );
}

function SettingsSection({
  id,
  title,
  code,
  rows,
  statusLabel,
  frontendLabel,
  versionLabel,
  children
}: {
  id: string;
  title: string;
  code: string;
  rows?: ActivitySettingRow[];
  statusLabel?: string;
  frontendLabel?: string;
  versionLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="settings-section" id={`settings-${id}`}>
      <div className="settings-section-head">
        <span>{code}</span>
        <h2>{title}</h2>
        <p>狀態：{statusLabel ?? activityStatus(rows)} / 前台：{frontendLabel ?? frontendStatus(rows)} / 版本：{versionLabel ?? versionText(rows)}</p>
      </div>
      {children}
      <details className="settings-advanced">
        <summary>進階 JSON 資料</summary>
        <p>此區為進階資料，設定改動只影響新產生的活動項目，不會自動修改已建立或已完成的派發紀錄。</p>
        <AdvancedJson rows={rows ?? []} />
      </details>
    </section>
  );
}

function InfoCard({ title, items }: { title: string; items: readonly string[] }) {
  return (
    <article className="settings-info-card">
      <h3>{title}</h3>
      <ul>{items.map((item) => <li key={item}>{item}</li>)}</ul>
    </article>
  );
}

function SimpleTable({ headers, rows }: { headers: readonly string[]; rows: readonly (readonly string[])[] }) {
  return (
    <div className="settings-table-wrap">
      <table className="settings-table">
        <thead>
          <tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>{row.map((cell, cellIndex) => <td key={`${index}-${cellIndex}`}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ label }: { label: string }) {
  return <em className={label.includes("啟用") || label.includes("完成") ? "settings-pill enabled" : "settings-pill"}>{label}</em>;
}

function AdvancedJson({ rows }: { rows: ActivitySettingRow[] }) {
  if (!rows.length) return <div className="settings-empty-json">目前沒有 activity_settings 原始 row；此區可能由 service 實作或尚未接設定表，請以上方目前真實規格展示為準。</div>;
  return (
    <pre className="settings-json-block">
      {JSON.stringify(rows.map((row) => ({ ...row, parsed_setting_json: parseSettingJson(row.setting_json) })), null, 2)}
    </pre>
  );
}

function groupRows(rows: ActivitySettingRow[]) {
  return rows.reduce<Record<string, ActivitySettingRow[]>>((acc, row) => {
    const code = String(row.activity_code ?? "unknown");
    acc[code] = [...(acc[code] ?? []), row];
    return acc;
  }, {});
}

function activityStatus(rows?: ActivitySettingRow[]) {
  if (!rows?.length) return "預留";
  return rows.some((row) => Number(row.is_enabled ?? 1) === 1) ? "啟用" : "停用";
}

function frontendStatus(rows?: ActivitySettingRow[]) {
  if (!rows?.length) return "未接設定";
  return rows.some((row) => Number(row.is_frontend_visible ?? 1) === 1) ? "顯示" : "隱藏";
}

function versionText(rows?: ActivitySettingRow[]) {
  if (!rows?.length) return "未接設定";
  return Array.from(new Set(rows.map((row) => String(row.version ?? "v1")))).join(" / ");
}

function updatedText(rows?: ActivitySettingRow[]) {
  if (!rows?.length) return "尚無";
  const values = rows.map((row) => String(row.updated_at ?? "")).filter(Boolean).sort();
  return values.at(-1) ?? "尚無";
}

function parseSettingJson(value?: string) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeLineLinkSettings(input: unknown): LineLinkSettings {
  if (!input || typeof input !== "object" || Array.isArray(input)) return emptyLineLinkSettings;
  const row = input as Record<string, unknown>;
  return {
    line_guild_leader_url: String(row.line_guild_leader_url ?? ""),
    line_official_url: String(row.line_official_url ?? ""),
    line_group_checkin_url: String(row.line_group_checkin_url ?? ""),
    invite_share_text: String(row.invite_share_text ?? ""),
    group_checkin_instruction_text: String(row.group_checkin_instruction_text ?? "")
  };
}

function validateLineLinkSettings(input: LineLinkSettings) {
  const urlFields = [
    ["line_guild_leader_url", "公會長 LINE 連結"],
    ["line_official_url", "官方 LINE 連結"],
    ["line_group_checkin_url", "指定 LINE 群打卡連結"]
  ] as const;
  for (const [key, label] of urlFields) {
    const value = input[key].trim();
    if (!value) continue;
    if (!isAllowedLineUrl(value)) return `${label}格式不正確，只允許 lin.ee、line.me、page.line.me 或 line:// 連結`;
  }
  return "";
}

function isAllowedLineUrl(value: string) {
  return value.startsWith("https://lin.ee/")
    || value.startsWith("https://line.me/")
    || value.startsWith("https://page.line.me/")
    || value.startsWith("line://");
}
